import { installationUpdatePayload, shouldUpdateInstallationBillingConfig } from "./installationEditPayload";
import { useAuth } from "@/_core/hooks/useAuth";
import { InstallationAddress } from "@/components/installations/InstallationAddress";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverAnchor,
} from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { Plus, Search, FileDown, Check, ChevronsUpDown, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { useLocation, useSearch } from "wouter";
import { parseInstallationFilters, installationListPath, installationDetailPath, installationEditorClosePath, type InstallationListFilters } from "@/lib/installationListNavigation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  installationMatchesSearch,
  resolveInstallationTitle,
  resolveClientDisplayInfo,
  resolveCodeDisplayInfo,
  resolveSiteAndAddressDisplayInfo,
  resolvePmAndDatesDisplayInfo,
  resolveWorkOrderTypeLabel,
  resolveStatusDisplayInfo,
} from "@/lib/installationContext";
import {
  canViewContractEconomics,
  formatCents,
  parseMoneyToCents,
  summarizeContractCeiling,
  type ContractAdjustmentView,
  type ContractCeilingView,
} from "@/components/installations/contractAdjustmentsUtils";
import { formatContractDeadlineDetails } from "./calendarUtils";

// Tipos locales para Installations.tsx
type CsvRow = (string | number)[];
type InstallationRow = {
  id: number;
  contractId?: number | null;
  contractCode?: string | null;
  contractualEndDate?: string | null;
  clientName: string;
  clientId: string;
  name?: string | null;
  siteName?: string | null;
  canonicalCustomerPartnerId?: number | null;
  canonicalCustomerName?: string | null;
  installationCode?: string | null;
  ticketReference?: string | null;
  clientDocument?: string;
  clientPhone?: string;
  clientEmail?: string;
  address: string;
  installationType: string;
  workOrderType: "installation" | "breakdown" | "maintenance";
  workDescription?: string;
  budget?: string;
  startDate?: string;
  endDate?: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  assignedTechnicianIds: number[];
  installationPrice?: string;
  originalContractAmount?: string;
  approvedIncreaseAmount?: string;
  approvedReductionAmount?: string;
  approvedNetDelta?: string;
  effectiveContractAmount?: string | null;
  committedCertificationsAmount?: string | null;
  availableContractAmount?: string | null;
  certificationEconomicReviewRequired?: boolean;
  certificationEconomicReviewMessage?: string | null;
  laborPrice?: string;
  contractDatesFixed: boolean;
  contractStatus: string;
  billingConfigStatus: string;
  billingRequirementMode: string;
  includesMaintenance?: boolean;
  maintenancePaymentStatus: string;
};
type TechnicianRow = {
  id: number;
  name: string;
  email: string;
};

type InitialBillingMilestoneForm = {
  label: string;
  fixedAmount: string;
  percentage: string;
  dueDate: string;
  description: string;
  calculatedAmount: string;
  paymentMethod: string;
  paymentTermBasis: string;
  paymentTerms: string;
  notes: string;
  type: 'fixed_amount' | 'percentage' | 'monthly_certification';
};

export type PlanningSegmentForm = {
  id?: number;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
};

export function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return "";
  const clean = dateStr.split("T")[0];
  const parts = clean.split("-");
  if (parts.length < 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function PlanningSegmentsEditor({
  segments,
  onChange,
}: {
  segments: PlanningSegmentForm[];
  onChange: (segments: PlanningSegmentForm[]) => void;
}) {
  const addPhase = () => {
    onChange([
      ...segments,
      {
        title: segments.length === 0 ? "Planificación general" : `Fase ${segments.length + 1}`,
        description: "",
        startDate: "",
        endDate: "",
      },
    ]);
  };

  const updateSegment = (index: number, field: keyof PlanningSegmentForm, value: string) => {
    const next = [...segments];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const removeSegment = (index: number) => {
    const next = segments.filter((_, i) => i !== index);
    onChange(next);
  };

  const stats = useMemo(() => {
    const valid = segments.filter((s) => s.startDate && s.endDate && s.endDate >= s.startDate);
    if (valid.length === 0) {
      return {
        totalPhases: segments.length,
        firstDate: null,
        lastDate: null,
        totalDaysProgrammed: 0,
        totalGapDays: 0,
        hasOverlap: false,
      };
    }

    const sorted = [...valid].sort((a, b) => a.startDate.localeCompare(b.startDate));

    let hasOverlap = false;
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].endDate >= sorted[i + 1].startDate) {
        hasOverlap = true;
        break;
      }
    }

    let minStart = sorted[0].startDate;
    let maxEnd = sorted[0].endDate;
    let totalDaysProgrammed = 0;

    sorted.forEach((s) => {
      if (s.startDate < minStart) minStart = s.startDate;
      if (s.endDate > maxEnd) maxEnd = s.endDate;

      const sParts = s.startDate.split("-").map(Number);
      const eParts = s.endDate.split("-").map(Number);
      const startMs = Date.UTC(sParts[0], sParts[1] - 1, sParts[2]);
      const endMs = Date.UTC(eParts[0], eParts[1] - 1, eParts[2]);
      const days = Math.round((endMs - startMs) / (1000 * 3600 * 24)) + 1;
      totalDaysProgrammed += Math.max(1, days);
    });

    let totalGapDays = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      const curEParts = sorted[i].endDate.split("-").map(Number);
      const nextSParts = sorted[i + 1].startDate.split("-").map(Number);
      const curEndMs = Date.UTC(curEParts[0], curEParts[1] - 1, curEParts[2]);
      const nextStartMs = Date.UTC(nextSParts[0], nextSParts[1] - 1, nextSParts[2]);
      const gap = Math.round((nextStartMs - curEndMs) / (1000 * 3600 * 24)) - 1;
      if (gap > 0) {
        totalGapDays += gap;
      }
    }

    return {
      totalPhases: segments.length,
      firstDate: formatDisplayDate(minStart),
      lastDate: formatDisplayDate(maxEnd),
      totalDaysProgrammed,
      totalGapDays,
      hasOverlap,
    };
  }, [segments]);

  return (
    <div className="space-y-4 rounded-lg border p-4 bg-slate-50/50">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">Planificación de la instalación</h3>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              Fases de programación
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Divide la instalación en los periodos en los que realmente está previsto trabajar. Los días entre fases quedarán libres en el calendario.
          </p>
        </div>
        <Button type="button" size="sm" onClick={addPhase} className="self-start sm:self-auto flex items-center gap-1">
          <Plus className="h-4 w-4" /> Añadir fase
        </Button>
      </div>

      {stats.hasOverlap && (
        <div className="p-3 text-xs rounded bg-amber-50 text-amber-900 border border-amber-300 font-medium flex items-center gap-2">
          <span>⚠️ Advertencia: Existen fases con fechas superpuestas en esta instalación.</span>
        </div>
      )}

      {segments.length > 0 && stats.firstDate && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-white p-3 rounded border shadow-sm">
          <div>
            <span className="font-semibold text-slate-500 block">Total fases</span>
            <span className="text-slate-900 font-medium">{stats.totalPhases}</span>
          </div>
          <div>
            <span className="font-semibold text-slate-500 block">Rango total</span>
            <span className="text-slate-900 font-medium">{stats.firstDate} - {stats.lastDate}</span>
          </div>
          <div>
            <span className="font-semibold text-slate-500 block">Días programados</span>
            <span className="text-slate-900 font-medium">{stats.totalDaysProgrammed} días</span>
          </div>
          <div>
            <span className="font-semibold text-slate-500 block">Días libres (huecos)</span>
            <span className="text-slate-900 font-medium">{stats.totalGapDays} días</span>
          </div>
        </div>
      )}

      {segments.length === 0 ? (
        <div className="text-center py-6 border border-dashed rounded-lg text-slate-400 text-sm bg-white">
          No hay fases de programación definidas. Haz clic en &quot;Añadir fase&quot; para definir los periodos de trabajo.
        </div>
      ) : (
        <div className="space-y-3">
          {segments.map((seg, idx) => {
            const titleErr = !seg.title.trim() ? "El título de la fase es obligatorio" : null;
            const startErr = !seg.startDate ? "La fecha de inicio es obligatoria" : null;
            const endErr = !seg.endDate ? "La fecha de fin es obligatoria" : null;
            const rangeErr = seg.startDate && seg.endDate && seg.endDate < seg.startDate
              ? "La fecha de fin no puede ser anterior a la fecha de inicio"
              : null;

            return (
              <div key={idx} className="p-3 bg-white border rounded-md shadow-sm space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-xs font-semibold text-slate-700">Fase {idx + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                    onClick={() => removeSegment(idx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Título de la fase *</Label>
                    <Input
                      className="h-8 text-xs"
                      value={seg.title}
                      onChange={(e) => updateSegment(idx, "title", e.target.value)}
                      placeholder="Ej. Planificación general, Montaje, OCA..."
                    />
                    {titleErr && <span className="text-[11px] text-red-600 font-medium">{titleErr}</span>}
                  </div>

                  <div className="grid gap-1">
                    <Label className="text-xs">Descripción (opcional)</Label>
                    <Input
                      className="h-8 text-xs"
                      value={seg.description || ""}
                      onChange={(e) => updateSegment(idx, "description", e.target.value)}
                      placeholder="Notas o detalles de la fase"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Fecha inicio *</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={seg.startDate}
                      onChange={(e) => updateSegment(idx, "startDate", e.target.value)}
                    />
                    {startErr && <span className="text-[11px] text-red-600 font-medium">{startErr}</span>}
                  </div>

                  <div className="grid gap-1">
                    <Label className="text-xs">Fecha fin *</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={seg.endDate}
                      onChange={(e) => updateSegment(idx, "endDate", e.target.value)}
                    />
                    {endErr && <span className="text-[11px] text-red-600 font-medium">{endErr}</span>}
                  </div>
                </div>

                {rangeErr && (
                  <div className="text-[11px] text-red-600 font-medium bg-red-50 p-2 rounded border border-red-200">
                    {rangeErr}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Installations() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [directChoiceOpen, setDirectChoiceOpen] = useState(false);
  const utils = trpc.useUtils();

  // Filters
  const listSearch = useSearch();
  const filters = parseInstallationFilters(listSearch);
  const { search: searchQuery, types: filterWorkOrderTypes, statuses: filterStatuses, from: startDate, to: endDate, technician: filterTechnician } = filters;
  const updateFilters = (patch: Partial<InstallationListFilters>) => {
    setLocation(installationListPath({ ...filters, ...patch }, listSearch), { replace: true });
  };


  const installationsQuery = trpc.installations.list.useQuery();
  useEffect(() => {
    if (installationsQuery.isError) {
      const errorMsg = installationsQuery.error?.message || 'Error desconocido';
      const sanitizedMsg = errorMsg.replace(/(https?:\/\/[^\s]+)/g, '[REDACTED]');
      console.error('Error al cargar las instalaciones:', sanitizedMsg);
    }
  }, [installationsQuery.isError, installationsQuery.error]);

  const techniciansQuery = trpc.users.listTechnicians.useQuery(undefined, {
    enabled: ['admin', 'project_manager', 'admin_manager'].includes(user?.role || ''),
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingOriginalInstallationPrice, setEditingOriginalInstallationPrice] = useState<string>("");

  // Contract upload state
  const [selectedContractFile, setSelectedContractFile] = useState<File | null>(null);
  const [tempInstallationId, setTempInstallationId] = useState<number | null>(null);

  const updateMutation = trpc.installations.update.useMutation({
    onSuccess: () => {
      utils.installations.list.invalidate();
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success("Instalación actualizada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al actualizar la instalación");
    }
  });

  const updateOperationalMutation = trpc.installations.updateOperational.useMutation({
    onSuccess: () => {
      utils.installations.list.invalidate();
      utils.customers.contracts.listInstallations.invalidate();
    },
    onError: (error) => toast.error(error.message || "Error al guardar la planificación operativa"),
  });

  const createWithBillingMutation = trpc.installations.createWithBilling.useMutation({
    onSuccess: () => {
      utils.installations.list.invalidate();
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success("Instalación creada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear la instalación con facturación");
    },
  });

  const updateBillingConfigMutation = trpc.installations.updateBillingConfig.useMutation({
    onSuccess: () => {
      utils.installations.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Error al actualizar la configuración de facturación");
    },
  });

  const documentUploadMutation = trpc.documents.upload.useMutation({
    onError: (error) => {
      toast.error(error.message || "Error al subir el documento");
    },
  });

  const deleteMutation = trpc.installations.delete.useMutation({
    onSuccess: () => {
      utils.installations.list.invalidate();
      toast.success("Instalación eliminada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar la instalación");
    }
  });

  const [planningSegments, setPlanningSegments] = useState<PlanningSegmentForm[]>([]);


  const resetForm = () => {
    setFormData({
      clientName: "",
      clientId: "",
      clientDocument: "",
      clientPhone: "",
      clientEmail: "",
      address: "",
      installationType: "",
      workOrderType: "installation",
      workDescription: "",
      budget: "",
      startDate: "",
      endDate: "",
      assignedTechnicianIds: [],
      installationPrice: "",
      laborPrice: "",
      contractDatesFixed: false,
      contractStatus: "contract_pending",
      billingConfigStatus: "billing_not_configured",
      billingRequirementMode: "milestones_required",
      includesMaintenance: false,
      maintenancePaymentStatus: "account_current",
    });
    setInitialBillingMilestones([]);
    setPlanningSegments([]);
    setEditingId(null);
    setEditingOriginalInstallationPrice("");
    setSelectedContractFile(null);
    setTempInstallationId(null);
  };

  const closeEditDialog = () => {
    const returnPath = installationEditorClosePath(listSearch);
    setIsCreateDialogOpen(false);
    resetForm();
    if (returnPath) setLocation(returnPath, { replace: true });
  };

  const handleEdit = (inst: InstallationRow) => {
    setEditingId(inst.id);
    setEditingOriginalInstallationPrice(inst.installationPrice ? String(inst.installationPrice) : "");
    setFormData({
      clientName: inst.clientName,
      clientId: inst.clientId,
      clientDocument: inst.clientDocument || "",
      clientPhone: inst.clientPhone || "",
      clientEmail: inst.clientEmail || "",
      address: inst.address,
      installationType: inst.installationType || "",
      workOrderType: inst.workOrderType,
      workDescription: inst.workDescription || "",
      budget: inst.budget || "",
      startDate: inst.startDate ? new Date(inst.startDate).toISOString().split('T')[0] : "",
      endDate: inst.endDate ? new Date(inst.endDate).toISOString().split('T')[0] : "",
      assignedTechnicianIds: Array.isArray(inst.assignedTechnicianIds) ? inst.assignedTechnicianIds : [],
      installationPrice: inst.installationPrice ? String(inst.installationPrice) : "",
      laborPrice: inst.laborPrice ? String(inst.laborPrice) : "",
      contractDatesFixed: inst.contractDatesFixed || false,
      contractStatus: (inst as any).contractStatus || "contract_pending",
      billingConfigStatus: (inst as any).billingConfigStatus || "billing_not_configured",
      billingRequirementMode: (inst as any).billingRequirementMode || "milestones_required",
      includesMaintenance: (inst as any).includesMaintenance === true,
      maintenancePaymentStatus: (inst as any).maintenancePaymentStatus || "account_current",
    });
    setInitialBillingMilestones([]);
    if (Array.isArray((inst as any).planningSegments) && (inst as any).planningSegments.length > 0) {
      setPlanningSegments(
        (inst as any).planningSegments.map((s: any) => ({
          id: s.id,
          title: s.title,
          description: s.description || "",
          startDate: String(s.startDate).split("T")[0],
          endDate: String(s.endDate).split("T")[0],
        }))
      );
    } else if (inst.startDate && inst.endDate) {
      const sStr = new Date(inst.startDate).toISOString().split("T")[0];
      const eStr = new Date(inst.endDate).toISOString().split("T")[0];
      setPlanningSegments([
        { title: "Planificación general", description: "", startDate: sStr, endDate: eStr }
      ]);
    } else {
      setPlanningSegments([]);
    }
    setIsCreateDialogOpen(true);
  };

  // Contract detail deep-links into this same editor via ?edit=<installationId>.
  useEffect(() => {
    if (editingId !== null || !installationsQuery.data) return;
    const params = new URLSearchParams(listSearch);
    const requestedId = Number(params.get("edit"));
    if (!Number.isInteger(requestedId) || requestedId <= 0) return;
    const installation = installationsQuery.data.find((row: InstallationRow) => row.id === requestedId);
    if (installation && ["admin", "project_manager", "admin_manager"].includes(user?.role || "")) {
      // Keep the deep-link while the editor is open; closeEditDialog removes it on every exit path.
      handleEdit(installation);
      return;
    }
    params.delete("edit");
    setLocation(`/installations${params.size ? `?${params.toString()}` : ""}`, { replace: true });
    if (!installation) toast.error("No se encontró la instalación solicitada");
  }, [editingId, installationsQuery.data, listSearch, setLocation, user?.role]);

  const handleDelete = (id: number) => {
    if (confirm("¿Estás seguro de que deseas eliminar esta instalación? Esta acción no se puede deshacer.")) {
      deleteMutation.mutate({ id });
    }
  };

  const [formData, setFormData] = useState<{
    clientName: string;
    clientId: string;
    clientDocument: string;
    clientPhone: string;
    clientEmail: string;
    address: string;
    installationType: string;
    workOrderType: string;
    workDescription: string;
    budget: string;
    startDate: string;
    endDate: string;
    assignedTechnicianIds: number[];
    installationPrice: string;
    laborPrice: string;
    contractDatesFixed: boolean;
    contractStatus: string;
    billingConfigStatus: string;
    billingRequirementMode: string;
    includesMaintenance: boolean;
    maintenancePaymentStatus: string;
  }>({
    clientName: "",
    clientId: "",
    clientDocument: "",
    clientPhone: "",
    clientEmail: "",
    address: "",
    installationType: "",
    workOrderType: "installation",
    workDescription: "",
    budget: "",
    startDate: "",
    endDate: "",
    assignedTechnicianIds: [],
    installationPrice: "",
    laborPrice: "",
    contractDatesFixed: false,
    contractStatus: "contract_pending",
    billingConfigStatus: "billing_not_configured",
    billingRequirementMode: "milestones_required",
    includesMaintenance: false,
    maintenancePaymentStatus: "account_current",
  });

  const [initialBillingMilestones, setInitialBillingMilestones] = useState<InitialBillingMilestoneForm[]>([]);
  const [initialMilestonesError, setInitialMilestonesError] = useState<string | null>(null);
  const initialMilestonesRef = useRef<HTMLDivElement>(null);

  const showInitialMilestonesError = (message: string) => {
    setInitialMilestonesError(message);
    toast.error(message);
    initialMilestonesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const isManager = ['admin', 'project_manager', 'admin_manager'].includes(user?.role || '');
  const isContractAdmin = ['admin', 'admin_manager'].includes(user?.role || '');
  const canViewContract = canViewContractEconomics(user?.role);
  const editContractAdjustmentsQuery = trpc.contractAdjustments.listByInstallation.useQuery(
    { installationId: editingId || 0 },
    { enabled: Boolean(editingId && canViewContract) },
  );
  const editContractCeilingQuery = trpc.contractAdjustments.getEffectiveContractCeiling.useQuery(
    { installationId: editingId || 0 },
    { enabled: Boolean(editingId && canViewContract) },
  );
  const editAdjustments = (editContractAdjustmentsQuery.data || []) as ContractAdjustmentView[];
  const editCeiling = (editContractCeilingQuery.data || null) as ContractCeilingView;
  const editContractSummary = summarizeContractCeiling(editCeiling, formData.installationPrice, editAdjustments);
  const editInitialCents = editContractSummary.originalCents;
  const editCurrentCents = editContractSummary.effectiveContractCeilingCents;
  const isEditingCanonicalContractLinked = Boolean(
    editingId && installationsQuery.data?.find((installation) => installation.id === editingId)?.contractId,
  );
  const editingOriginalContractCents = parseMoneyToCents(editingOriginalInstallationPrice);
  const isEditingContractPriceEstablished = Boolean(editingId && (editingOriginalContractCents ?? 0) > 0);
  const canAssignMissingContractPrice = Boolean(editingId && !isEditingContractPriceEstablished && isContractAdmin);
  const canExportInstallations = isManager;
  const showBillingConfig = formData.workOrderType === 'installation' && isManager && !isEditingCanonicalContractLinked;
  const showMaintenancePaymentConfig = formData.includesMaintenance && isManager && !isEditingCanonicalContractLinked;
  const canAddInitialMilestones = showBillingConfig && !editingId;

  const addInitialBillingMilestone = () => {
    setInitialMilestonesError(null);
    setInitialBillingMilestones((items) => [
      ...items,
      { label: '', fixedAmount: '', percentage: '', dueDate: '', description: '', calculatedAmount: '', paymentMethod: 'transfer', paymentTermBasis: 'invoice_date', paymentTerms: '', notes: '', type: 'fixed_amount' as 'fixed_amount' | 'percentage' | 'monthly_certification' },
    ]);
  };

  const updateInitialBillingMilestone = (index: number, field: keyof InitialBillingMilestoneForm, value: string) => {
    setInitialMilestonesError(null);
    setInitialBillingMilestones((items) => items.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const removeInitialBillingMilestone = (index: number) => {
    setInitialMilestonesError(null);
    setInitialBillingMilestones((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInitialMilestonesError(null);
    if (editingId === null) {
      toast.error("El alta legacy está deshabilitada por el servidor. Utiliza Nueva obra o Trabajo sin contrato desde un cliente.");
      return;
    }

    const installationPricePayload = editingId
      ? (canAssignMissingContractPrice && formData.installationPrice ? { installationPrice: parseFloat(formData.installationPrice) } : {})
      : { installationPrice: formData.installationPrice ? parseFloat(formData.installationPrice) : undefined };

    const isContractLinkedInstallation = Boolean(
      installationsQuery.data?.find((installation) => installation.id === editingId)?.contractId,
    );
    const basePayload = installationUpdatePayload(isContractLinkedInstallation, {
      clientName: formData.clientName,
      clientId: formData.clientId,
      clientDocument: formData.clientDocument || undefined,
      clientPhone: formData.clientPhone || undefined,
      clientEmail: formData.clientEmail || undefined,
      address: formData.address,
      installationType: formData.installationType,
      workOrderType: formData.workOrderType as any,
      workDescription: formData.workDescription || undefined,
      budget: formData.budget || undefined,
      startDate: formData.startDate ? new Date(formData.startDate) : undefined,
      endDate: formData.endDate ? new Date(formData.endDate) : undefined,
      ...installationPricePayload,
      laborPrice: formData.laborPrice ? parseFloat(formData.laborPrice) : undefined,
      assignedTechnicianIds: formData.assignedTechnicianIds.length > 0 ? formData.assignedTechnicianIds : undefined,
      includesMaintenance: formData.includesMaintenance === true,
    });

    const billingPayload = {
      contractStatus: formData.contractStatus as any,
      billingConfigStatus: formData.billingConfigStatus as any,
      billingRequirementMode: formData.billingRequirementMode as any,
      includesMaintenance: formData.includesMaintenance === true,
      maintenancePaymentStatus: formData.includesMaintenance ? formData.maintenancePaymentStatus as any : undefined,
    };

    const validPlanningSegments = planningSegments
      .filter((s) => s.title.trim() && s.startDate && s.endDate)
      .map((s) => ({
        id: s.id,
        title: s.title.trim(),
        description: s.description || undefined,
        startDate: s.startDate,
        endDate: s.endDate,
      }));

    if (editingId) {
      if (isContractLinkedInstallation) {
        await updateOperationalMutation.mutateAsync({
          id: editingId,
          installationType: formData.installationType || null,
          workOrderType: formData.workOrderType as any,
          workDescription: formData.workDescription || null,
          startDate: formData.startDate ? new Date(formData.startDate) : null,
          endDate: formData.endDate ? new Date(formData.endDate) : null,
          laborPrice: formData.laborPrice ? parseFloat(formData.laborPrice) : null,
          assignedTechnicianIds: formData.assignedTechnicianIds,
          planningSegments: validPlanningSegments,
        });
      } else {
        await updateMutation.mutateAsync({
          id: editingId,
          ...basePayload,
          planningSegments: validPlanningSegments,
        });
      }
      if (shouldUpdateInstallationBillingConfig(
        isContractLinkedInstallation,
        showBillingConfig,
        showMaintenancePaymentConfig,
      )) {
        await updateBillingConfigMutation.mutateAsync({ id: editingId, ...billingPayload });
      }
      utils.installations.list.invalidate();
      closeEditDialog();
      toast.success("Instalación actualizada exitosamente");
      return;
    }

    const installationPrice = formData.installationPrice ? parseFloat(formData.installationPrice) : undefined;

    // Calculate calculatedAmount for percentage milestones based on installationPrice
    // monthly_certification permite label sin importe (se define manual durante certificación)
    const initialMilestones = canAddInitialMilestones
      ? initialBillingMilestones
        .filter((milestone) => milestone.label.trim() || milestone.fixedAmount.trim() || milestone.percentage.trim() || milestone.type === 'monthly_certification')
        .map((milestone) => {
          // Determine type explicitly - preserve monthly_certification
          const type = milestone.type;

          // Calculate calculatedAmount from installationPrice for percentage milestones
          let calculatedAmount = milestone.calculatedAmount;
          if (type === 'percentage' && installationPrice && milestone.percentage) {
            calculatedAmount = (installationPrice * parseFloat(milestone.percentage) / 100).toString();
          }

          return {
            type: type as 'fixed_amount' | 'percentage' | 'monthly_certification',
            label: milestone.label.trim(),
            fixedAmount: type === 'fixed_amount' ? milestone.fixedAmount : undefined,
            percentage: type === 'percentage' ? parseFloat(milestone.percentage) : undefined,
            calculatedAmount: calculatedAmount ? String(calculatedAmount) : undefined,
            dueDate: milestone.dueDate ? new Date(milestone.dueDate) : undefined,
            description: milestone.description || undefined,
            paymentMethod: milestone.paymentMethod as 'transfer' | 'confirming' | 'promissory_note' | 'other' | undefined,
            paymentTermBasis: milestone.paymentTermBasis as 'invoice_date' | 'end_of_month' | 'client_acceptance' | 'other' | undefined,
            paymentTerms: milestone.paymentTerms || undefined,
            notes: milestone.notes || undefined,
          };
        })
      : [];

    // Validation: percentage milestones require installationPrice when billing is validated
    const hasPercentageMilestones = initialMilestones.some(m => m.type === 'percentage');
    const hasFixedMilestones = initialMilestones.some(m => m.type === 'fixed_amount');

    // Calculate sums (monthly_certification no se cuenta como fixed_amount)
    let percentageSum = 0;
    let fixedAmountSum = 0;

    initialMilestones.forEach(m => {
      if (m.type === 'percentage' && m.percentage) {
        percentageSum += m.percentage;
      }
      if (m.type === 'fixed_amount' && m.fixedAmount) {
        fixedAmountSum += parseFloat(m.fixedAmount);
      }
      if (m.type === 'percentage' && m.calculatedAmount) {
        fixedAmountSum += parseFloat(m.calculatedAmount);
      }
    });

    const installationPriceNum = installationPrice || 0;
    const roundedFixedSum = Math.round(fixedAmountSum * 100) / 100;
    const roundedPrice = Math.round(installationPriceNum * 100) / 100;

    // Check for monthly_certification in validated mode (blocks - no fixed amount defined)
    const hasMonthlyCertification = initialMilestones.some(m => m.type === 'monthly_certification');

    // Reglas de suma SOLO para modelo 'Por hitos'
    if (formData.billingRequirementMode === 'milestones_required') {
      if (formData.billingConfigStatus === 'billing_validated') {
        // Validation mode: enforce rules strictly
        if (hasMonthlyCertification) {
          // monthly_certification bloquea en Validado (no tiene importe definido)
          showInitialMilestonesError('La certificación mensual no permite validación de facturación. El importe se define manualmente durante la certificación. Usa Provisional o reemplázala por hitos fijos/porcentaje.');
          return;
        }
        if (hasPercentageMilestones && !hasFixedMilestones) {
          // All percentages: must sum to 100
          if (Math.abs(percentageSum - 100) > 0.01) {
            showInitialMilestonesError('La suma de porcentajes debe ser del 100% antes de validar la facturación.');
            return;
          }
        } else {
          // Mixed or fixed only: sum of fixed amounts must equal installationPrice
          if (roundedFixedSum !== roundedPrice) {
            showInitialMilestonesError('La suma de los hitos debe coincidir con el Importe inicial del contrato antes de validar la facturación.');
            return;
          }
        }
      } else if (formData.billingConfigStatus === 'billing_provisional') {
        // Provisional mode: allow but warn
        if (hasPercentageMilestones && !hasFixedMilestones) {
          if (Math.abs(percentageSum - 100) > 0.01) {
            toast.info('La suma de porcentajes no es del 100%. Revisa antes de validar.');
          }
        } else if (roundedFixedSum !== roundedPrice) {
          toast.info('La suma de hitos no coincide con el Importe inicial del contrato. Revisa antes de validar.');
        }
        if (hasMonthlyCertification) {
          toast.info('La certificación mensual (monthly_certification) permite guardar sin importe. El importe se definirá manualmente durante la certificación.');
        }
      }
    }

    if (hasPercentageMilestones && !installationPrice && formData.billingConfigStatus === 'billing_validated') {
      showInitialMilestonesError('Para validar hitos por porcentaje debes indicar el Importe inicial del contrato (€) de la instalación.');
      return;
    }

    if (hasPercentageMilestones && !installationPrice && formData.billingConfigStatus === 'billing_provisional') {
      // Allow but show warning - calculation will happen when price is added
      toast.info('El importe se calculará cuando se indique el Importe inicial del contrato (€).');
    }

    if (
      formData.workOrderType === 'installation' &&
      formData.billingRequirementMode === 'milestones_required' &&
      formData.billingConfigStatus === 'billing_validated' &&
      initialMilestones.length === 0
    ) {
      showInitialMilestonesError('La facturación validada con hitos obligatorios requiere al menos un hito inicial');
      return;
    }

    // A) Contract signed requires document upload
    // Sequential flow when saving with Firmado+file:
    // 1. Create installation with contractStatus=contract_pending (temporary)
    // 2. Obtain installationId from response
    // 3. Upload document via documents.upload with documentType='contract'
    // 4. Get documentId from upload response
    // 5. Call updateBillingConfig with contractDocumentId and contractStatus='contract_signed'
    // 6. On any upload failure: leave as Pendiente, clear error, do NOT mark Firmado

    if (formData.contractStatus === 'contract_signed' && selectedContractFile) {
      // Block if no file selected
      if (!selectedContractFile) {
        toast.error('Debes seleccionar un archivo de contrato para firmar la instalación.');
        return;
      }

      // Step 1: Create installation with contract_pending and planningSegments atomically
      const createResult = await createWithBillingMutation.mutateAsync({
        ...basePayload,
        billingConfigStatus: formData.billingConfigStatus as any,
        billingRequirementMode: formData.billingRequirementMode as any,
        maintenancePaymentStatus: formData.maintenancePaymentStatus as any,
        contractStatus: 'contract_pending',
        ...((showBillingConfig || showMaintenancePaymentConfig) ? billingPayload : {}),
        initialMilestones,
        planningSegments: validPlanningSegments,
      });

      // Step 2: Get installationId from response
      const installationId = createResult.installationId;
      if (!installationId) {
        toast.error('Error al crear la instalación. No se pudo obtener el ID.');
        return;
      }

      // Step 3: Upload contract document
      const fileReader = new FileReader();
      fileReader.onload = async () => {
        const fileData = fileReader.result as string;
        const base64Data = fileData.split(',')[1] || fileData;

        try {
          const uploadResult = await documentUploadMutation.mutateAsync({
            installationId,
            name: selectedContractFile.name,
            documentType: 'contract',
            fileData: base64Data,
            mimeType: selectedContractFile.type,
          });

          if (!uploadResult.success || !uploadResult.documentId) {
            toast.error('Error al subir el contrato. El contrato permanecerá como Pendiente.');
            return;
          }

          // Step 4 & 5: Update billing config with contractDocumentId and contract_signed
          await updateBillingConfigMutation.mutateAsync({
            id: installationId,
            contractStatus: 'contract_signed',
            contractDocumentId: uploadResult.documentId,
            billingConfigStatus: formData.billingConfigStatus as any,
            billingRequirementMode: formData.billingRequirementMode as any,
            maintenancePaymentStatus: formData.maintenancePaymentStatus as any,
          });

          toast.success('Instalación creada y contrato subido exitosamente');
          utils.installations.list.invalidate();
          setIsCreateDialogOpen(false);
          resetForm();
        } catch (error) {
          toast.error('Error al subir el contrato. El contrato permanecerá como Pendiente.');
        }
      };
      fileReader.onerror = () => {
        toast.error('Error al leer el archivo de contrato.');
      };
      fileReader.readAsDataURL(selectedContractFile);

      // Don't continue with normal mutation - the fileReader.onload handles everything
      return;
    }

    const createResult = await createWithBillingMutation.mutateAsync({
      ...basePayload,
      ...((showBillingConfig || showMaintenancePaymentConfig) ? billingPayload : {}),
      initialMilestones,
      planningSegments: validPlanningSegments,
    });

    toast.success("Instalación creada exitosamente");
    utils.installations.list.invalidate();
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const canCreateInstallations = ['admin', 'project_manager', 'admin_manager'].includes(user?.role || '');

  // Filter Logic
  const filteredInstallations = installationsQuery.data?.filter((installation: InstallationRow) => {
    const matchesSearch = installationMatchesSearch(installation as any, searchQuery);

    const matchesType = filterWorkOrderTypes.length === 0 || filterWorkOrderTypes.includes((installation as any).workOrderType);
    const matchesStatus = filterStatuses.length === 0 || filterStatuses.includes(installation.status);

    // Date filter (Start Date)
    const matchesStartDate = !startDate || (installation.startDate && new Date(installation.startDate) >= new Date(startDate));
    const matchesEndDate = !endDate || (installation.startDate && new Date(installation.startDate) <= new Date(endDate));

    const matchesTechnician = !filterTechnician || (installation.assignedTechnicianIds ?? []).some(id => String(id) === filterTechnician);
    return matchesTechnician && matchesSearch && matchesType && matchesStatus && matchesStartDate && matchesEndDate;
  }) || [];

  // Export to Excel (CSV)
  const exportToExcel = () => {
    if (filteredInstallations.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = [
      "ID",
      "Cliente",
      "Nº expediente",
      "Dirección",
      "Tipo Orden",
      "Estado",
      "Fecha Inicio",
      "Fecha Fin",
      "M.O.",
      "Contrato original",
      "Ampliaciones aprobadas",
      "Reducciones aprobadas",
      "Variación aprobada neta",
      "Importe contractual vigente",
      "Certificaciones comprometidas",
      "Disponible del contrato",
      "Revisión económica",
    ];
    const rows: CsvRow[] = filteredInstallations.map((inst: InstallationRow) => [
      inst.id,
      inst.clientName,
      (inst as any).clientId || "",
      inst.address,
      (inst as any).workOrderType,
      inst.status,
      inst.startDate ? new Date(inst.startDate).toLocaleDateString() : "",
      inst.endDate ? new Date(inst.endDate).toLocaleDateString() : "",
      (inst as any).laborPrice || "",
      inst.originalContractAmount || (inst as any).installationPrice || "",
      inst.approvedIncreaseAmount ?? "",
      inst.approvedReductionAmount ?? "",
      inst.approvedNetDelta ?? "",
      inst.effectiveContractAmount || inst.originalContractAmount || "",
      inst.committedCertificationsAmount ?? "",
      inst.availableContractAmount ?? "",
      inst.certificationEconomicReviewRequired ? (inst.certificationEconomicReviewMessage || "Revisión requerida · importes derivados no disponibles") : "",
    ]);

    const escapeCsvValue = (value: string | number) => {
      const raw = String(value ?? "");
      return /[",\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
    };
    const csvContent = "data:text/csv;charset=utf-8,"
      + headers.map(escapeCsvValue).join(",") + "\n"
      + rows.map((row: CsvRow) => row.map(escapeCsvValue).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "instalaciones.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper for technician multi-select
  const [techComboOpen, setTechComboOpen] = useState(false);
  const toggleTechnician = (techId: number) => {
    const current = formData.assignedTechnicianIds;
    if (current.includes(techId)) {
      setFormData({ ...formData, assignedTechnicianIds: current.filter(id => id !== techId) });
    } else {
      setFormData({ ...formData, assignedTechnicianIds: [...current, techId] });
    }
  };

  return (
    <DashboardLayout>
      <div className="min-w-0 space-y-6">
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Instalaciones</h1>
            <p className="text-sm text-muted-foreground mt-2">Listado de obras</p>
            <p className="text-muted-foreground mt-2">
              Gestión de proyectos fotovoltaicos
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full lg:w-auto min-w-0 max-w-full justify-end">
            {canExportInstallations && (
              <Button variant="outline" className="w-full sm:w-auto min-w-0 justify-center" onClick={exportToExcel}>
                <FileDown className="w-4 h-4 mr-2 shrink-0" />
                <span className="min-w-0 text-center">Exportar a Excel</span>
              </Button>
            )}
            {canCreateInstallations && <Button variant="outline" onClick={() => setDirectChoiceOpen(true)}>Trabajo sin contrato / respaldo</Button>}
            {canCreateInstallations && (
              <Button className="w-full sm:w-auto min-w-0 justify-center" onClick={() => setLocation("/customers?newWork=1")}>
                <Plus className="w-4 h-4 mr-2 shrink-0" />
                <span className="min-w-0 text-center">Nueva obra</span>
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card p-4 rounded-lg border flex flex-col lg:flex-row lg:flex-wrap gap-4 lg:items-end">
          <div className="grid gap-2 w-full md:w-auto flex-1">
            <Label>Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nombre, nº expediente, dirección..."
                maxLength={200}
                className="pl-8"
                value={searchQuery}
                onChange={(e) => updateFilters({ search: e.target.value })}
              />
            </div>
          </div>
          <div className="grid gap-2 w-full md:w-64">
            <Label>Tipo de Orden</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-2 min-h-10">
              {[
                ['installation', 'Instalación'],
                ['breakdown', 'Avería'],
                ['maintenance', 'Mantenimiento'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-1 text-sm cursor-pointer">
                  <Checkbox
                    checked={filterWorkOrderTypes.includes(value)}
                    onCheckedChange={(checked) => updateFilters({ types: checked ? [...filterWorkOrderTypes, value] : filterWorkOrderTypes.filter(v => v !== value) })}
                  />
                  {label}
                </label>
              ))}
              {filterWorkOrderTypes.length > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => updateFilters({ types: [] })}>Todos</Button>}
            </div>
          </div>
          <div className="grid gap-2 w-full md:w-64">
            <Label>Estado</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-2 min-h-10">
              {[
                ['pending', 'Pendiente'],
                ['in_progress', 'Iniciada'],
                ['completed', 'Completada'],
                ['cancelled', 'Cancelada'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-1 text-sm cursor-pointer">
                  <Checkbox
                    checked={filterStatuses.includes(value)}
                    onCheckedChange={(checked) => updateFilters({ statuses: checked ? [...filterStatuses, value] : filterStatuses.filter(v => v !== value) })}
                  />
                  {label}
                </label>
              ))}
              {filterStatuses.length > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => updateFilters({ statuses: [] })}>Todos</Button>}
            </div>
          </div>
          {isManager && <div className="grid gap-2 w-full md:w-64">
            <Label htmlFor="installation-filter-technician">Técnico</Label>
            <Select value={filterTechnician || "all"} onValueChange={value => updateFilters({ technician: value === "all" ? "" : value })}>
              <SelectTrigger id="installation-filter-technician"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los técnicos</SelectItem>
                {filterTechnician && !(techniciansQuery.data ?? []).some((tech: TechnicianRow) => String(tech.id) === filterTechnician) && <SelectItem value={filterTechnician}>Técnico seleccionado no disponible</SelectItem>}
                {(techniciansQuery.data ?? []).map((tech: TechnicianRow) => <SelectItem key={tech.id} value={String(tech.id)}>{tech.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>}
          <div className="grid gap-2 w-full md:w-auto">
            <Label htmlFor="installation-filter-from">Desde</Label>
            <Input id="installation-filter-from" type="date" value={startDate} onChange={(e) => updateFilters({ from: e.target.value })} />
          </div>
          <div className="grid gap-2 w-full md:w-auto">
            <Label htmlFor="installation-filter-to">Hasta</Label>
            <Input id="installation-filter-to" type="date" value={endDate} onChange={(e) => updateFilters({ to: e.target.value })} />
          </div>
          <Button type="button" variant="outline" onClick={() => updateFilters(parseInstallationFilters(""))}>Limpiar filtros</Button>
        </div>

        {installationsQuery.isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando instalaciones...</p>
          </div>
        ) : installationsQuery.isError ? (
          <div className="text-center py-12 border rounded-lg bg-card p-6">
            <p className="text-destructive font-medium">Error al cargar las instalaciones</p>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => installationsQuery.refetch()}
            >
              Reintentar
            </Button>
          </div>
        ) : filteredInstallations.length === 0 ? (
          <div className="text-center py-12 border rounded-lg bg-card">
            <p className="text-muted-foreground">No se encontraron instalaciones con los filtros seleccionados.</p>
          </div>
        ) : (
          <div className="min-w-0 max-w-full overflow-hidden rounded-lg border bg-card">
            <div className="w-full overflow-x-auto [&>div]:overflow-visible" role="region" aria-label="Listado de obras: desplaza horizontalmente para ver todas las columnas" tabIndex={0}>
              <Table className="w-full min-w-[1310px] text-sm table-fixed">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[220px]">Instalación / Código</TableHead>
                    <TableHead className="w-[240px]">Cliente</TableHead>
                    <TableHead className="w-[220px]">Sede y Dirección</TableHead>
                    <TableHead className="w-[130px]">Tipo</TableHead>
                    <TableHead className="w-[130px]">Estado</TableHead>
                    <TableHead className="w-[190px]">PM / Fechas</TableHead>
                    <TableHead className="w-[180px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInstallations.map((inst: InstallationRow) => {
                    const title = resolveInstallationTitle(inst as any);
                    const clientDisplay = resolveClientDisplayInfo(inst as any);
                    const codeDisplay = resolveCodeDisplayInfo(inst as any);
                    const siteAddressDisplay = resolveSiteAndAddressDisplayInfo(inst as any);
                    const pmDatesDisplay = resolvePmAndDatesDisplayInfo(inst as any);
                    const workOrderLabel = resolveWorkOrderTypeLabel((inst as any).workOrderType);
                    const statusDisplay = resolveStatusDisplayInfo(inst.status);

                    return (
                      <TableRow
                        key={inst.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setLocation(installationDetailPath(inst.id, filters))}
                      >
                        {/* Instalación / Código */}
                        <TableCell className="max-w-[220px]">
                          <div className="font-medium text-slate-900 dark:text-slate-100 whitespace-normal break-words" title={title}>
                            {title}
                          </div>
                          {inst.certificationEconomicReviewRequired && (
                            <p role="status" className="mt-1 w-full max-w-full whitespace-normal break-words [overflow-wrap:anywhere] text-xs text-amber-800 dark:text-amber-300">Revisión económica pendiente: importes no disponibles</p>
                          )}
                          <div className="text-xs text-muted-foreground font-mono break-words flex items-center gap-1.5 flex-wrap mt-0.5">
                            {inst.workOrderType === "breakdown" && (inst as any).ticketReference ? (
                              <span>Ticket: {(inst as any).ticketReference}</span>
                            ) : codeDisplay.installationCode ? (
                              <span>Exp: {codeDisplay.installationCode}</span>
                            ) : codeDisplay.expedienteCode ? (
                              <span>Exp: {codeDisplay.expedienteCode}</span>
                            ) : (
                              <span>—</span>
                            )}
                            {codeDisplay.installationCode && codeDisplay.expedienteCode && codeDisplay.expedienteCode !== codeDisplay.installationCode && (
                              <span className="min-w-0 max-w-full break-all">Ref. legacy: {codeDisplay.expedienteCode}</span>
                            )}
                            {codeDisplay.contractCode && (
                              <Badge variant="outline" className="max-w-full whitespace-normal break-all text-[10px] px-1 py-0 min-h-4 h-auto font-mono font-normal">
                                {codeDisplay.contractCode}
                              </Badge>
                            )}
                          </div>
                        </TableCell>

                        {/* Cliente */}
                        <TableCell className="max-w-[240px]">
                          <div className="font-medium text-sm text-slate-900 dark:text-slate-100 whitespace-normal break-words" title={clientDisplay.name}>
                            {clientDisplay.name}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                            <Badge
                              variant={clientDisplay.provenance === "customer360" ? "secondary" : "outline"}
                              className={cn(
                                "text-[10px] px-1 py-0 h-4 font-medium",
                                clientDisplay.provenance === "customer360"
                                  ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
                                  : ""
                              )}
                            >
                              {clientDisplay.provenanceLabel}
                            </Badge>
                            {clientDisplay.customerCode && (
                              <span className="font-mono text-[11px] text-muted-foreground">({clientDisplay.customerCode})</span>
                            )}
                          </div>
                        </TableCell>

                        {/* Sede y Dirección */}
                        <TableCell className="max-w-[220px]">
                          {siteAddressDisplay.siteName && (
                            <div className="font-medium text-xs text-slate-800 dark:text-slate-200 whitespace-normal break-words" title={siteAddressDisplay.siteName}>
                              {siteAddressDisplay.siteName}
                            </div>
                          )}
                          <div
                            className={cn(
                              "text-xs whitespace-normal break-words",
                              siteAddressDisplay.siteName ? "text-muted-foreground" : "font-medium text-slate-800 dark:text-slate-200"
                            )}
                            title={siteAddressDisplay.address}
                          >
                            <InstallationAddress installation={inst} />
                          </div>
                        </TableCell>

                        {/* Tipo */}
                        <TableCell className="whitespace-nowrap">
                          <Badge variant="outline" className="text-xs font-normal">
                            {workOrderLabel}
                          </Badge>
                        </TableCell>

                        {/* Estado */}
                        <TableCell className="whitespace-nowrap">
                          <Badge className={cn("text-xs text-white", statusDisplay.variantClass)}>
                            {statusDisplay.label}
                          </Badge>
                        </TableCell>

                        {/* PM / Fechas */}
                        <TableCell className="max-w-[140px]">
                          <div className="text-xs font-medium text-slate-800 dark:text-slate-200 whitespace-normal break-words" title={pmDatesDisplay.projectManagerName}>
                            {pmDatesDisplay.projectManagerName}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate" title={pmDatesDisplay.dateRangeFormatted || "—"}>
                            {pmDatesDisplay.dateRangeFormatted || "—"}
                          </div>
                        </TableCell>

                        {/* Acciones */}
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs"
                              onClick={() => setLocation(installationDetailPath(inst.id, filters))}
                            >
                              Ver
                            </Button>
                            {canCreateInstallations && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-500 hover:text-blue-600"
                                  aria-label="Editar instalación"
                                  onClick={() => handleEdit(inst)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(inst.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={directChoiceOpen} onOpenChange={setDirectChoiceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alta directa excepcional</DialogTitle>
            <DialogDescription>Para una obra normal utiliza Nueva obra: Cliente → Contrato → Instalación. El alta directa no crea ni vincula automáticamente un contrato.</DialogDescription>
          </DialogHeader>
          <Button variant="outline" className="h-auto min-h-11 whitespace-normal" onClick={() => {
            setDirectChoiceOpen(false); setLocation("/customers?directWork=1");
          }}>Avería o mantenimiento sin contrato</Button>
          <p className="text-sm text-muted-foreground">Para asociar cliente y sede, usa Trabajo sin contrato en la ficha del cliente. Las garantías no tienen un tipo propio: no se clasifican automáticamente.</p>
          <Button variant="outline" className="h-auto min-h-11 whitespace-normal" onClick={() => {
            resetForm();
            setDirectChoiceOpen(false); setIsCreateDialogOpen(true);
          }}>Consultar formulario de respaldo legacy</Button>
          <p className="text-sm text-muted-foreground">Formulario conservado durante la validación de paridad. El servidor ya no permite crear desde este respaldo; no se reactiva esa vía.</p>
        </DialogContent>
      </Dialog>
      {/* Create Installation Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (open) setIsCreateDialogOpen(true);
          else if (editingId !== null) closeEditDialog();
          else setIsCreateDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Instalación" : "Formulario de respaldo legacy"}</DialogTitle>
            <DialogDescription>
              {editingId ? "Modificar los detalles del proyecto" : "Alta excepcional; para una obra normal utiliza Nueva obra"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {!editingId && <p className="mb-4 text-sm text-muted-foreground">Referencia de respaldo: el servidor exige el flujo canónico y no permite guardar nuevas instalaciones aquí. La edición de instalaciones existentes sigue disponible.</p>}

            <div className="grid gap-6 py-4">
              {/* Sección Cliente */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-1">{isEditingCanonicalContractLinked ? "Cliente del contrato" : "Datos legacy del cliente"}</h3>
                {isEditingCanonicalContractLinked ? (
                  <p className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                    {installationsQuery.data?.find((row) => row.id === editingId)?.canonicalCustomerName || formData.clientName}
                    {" · "}Contrato {installationsQuery.data?.find((row) => row.id === editingId)?.contractCode || "canónico"}.
                    La identidad y los datos de contacto se gestionan en Cliente/Contrato.
                  </p>
                ) : (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="clientName">Nombre del Cliente *</Label>
                    <Input id="clientName" value={formData.clientName} onChange={e => setFormData({ ...formData, clientName: e.target.value })} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="clientId">Nº expediente legacy *</Label>
                    <Input id="clientId" value={formData.clientId} onChange={e => setFormData({ ...formData, clientId: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="clientDocument">DNI/NIE/CIF</Label>
                    <Input id="clientDocument" value={formData.clientDocument} onChange={e => setFormData({ ...formData, clientDocument: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="clientPhone">Teléfono</Label>
                    <Input id="clientPhone" value={formData.clientPhone} onChange={e => setFormData({ ...formData, clientPhone: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="clientEmail">Email</Label>
                    <Input id="clientEmail" type="email" value={formData.clientEmail} onChange={e => setFormData({ ...formData, clientEmail: e.target.value })} />
                  </div>
                </div>
                </>
                )}
              </div>

              {/* Sección Instalación */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-1">Detalles de la Instalación</h3>
                <div className="grid gap-2">
                  <Label htmlFor="address">Dirección *</Label>
                  <Input id="address" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} required readOnly={isEditingCanonicalContractLinked} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="workOrderType">Tipo de Orden *</Label>
                    <Select value={formData.workOrderType} onValueChange={v => setFormData({ ...formData, workOrderType: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="installation">Instalación</SelectItem>
                        <SelectItem value="breakdown">Avería</SelectItem>
                        <SelectItem value="maintenance">Mantenimiento</SelectItem>
                      </SelectContent>
                    </Select>
                    {isManager && !isEditingCanonicalContractLinked && (
                      <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="includesMaintenanceInline"
                            checked={formData.includesMaintenance}
                            onCheckedChange={(checked) => setFormData({ ...formData, includesMaintenance: checked === true })}
                          />
                          <Label htmlFor="includesMaintenanceInline">¿Incluye mantenimiento?</Label>
                        </div>
                        {formData.includesMaintenance && (
                          <Select
                            value={formData.maintenancePaymentStatus}
                            onValueChange={v => setFormData({ ...formData, maintenancePaymentStatus: v })}
                          >
                            <SelectTrigger id="maintenancePaymentStatusInline"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="account_current">Cuenta corriente</SelectItem>
                              <SelectItem value="payment_pending">Pendiente</SelectItem>
                              <SelectItem value="payment_overdue">Atrasado</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <p className="text-xs text-muted-foreground">
                          El estado de pago solo se usa si el mantenimiento está marcado explícitamente.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="installationType">Tipo de Instalación</Label>
                    <Input id="installationType" value={formData.installationType} onChange={e => setFormData({ ...formData, installationType: e.target.value })} placeholder="Ej. Solar, Aerotermia..." />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="workDescription">Descripción del Trabajo</Label>
                  <Textarea id="workDescription" value={formData.workDescription} onChange={e => setFormData({ ...formData, workDescription: e.target.value })} rows={3} />
                </div>
              </div>

              {/* Tiempos y Personal */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-1">Planificación y Técnicos</h3>
                <PlanningSegmentsEditor segments={planningSegments} onChange={setPlanningSegments} />

                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label>Asignar Técnicos</Label>
                    {techniciansQuery.data && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {techniciansQuery.data.length} técnicos disponibles
                      </span>
                    )}
                  </div>
                  <Popover open={techComboOpen} onOpenChange={setTechComboOpen} modal={false}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={techComboOpen} className="justify-between w-full h-auto min-h-[40px] py-2">
                        <div className="flex flex-wrap gap-1">
                          {formData.assignedTechnicianIds.length > 0 ? (
                            formData.assignedTechnicianIds.map(id => (
                              <Badge key={id} variant="secondary" className="mr-1">
                                {techniciansQuery.data?.find((t: TechnicianRow) => t.id === id)?.name || id}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground">Seleccionar técnicos...</span>
                          )}
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[400px] p-0 z-[99999] shadow-2xl border-2 pointer-events-auto"
                      align="start"
                      side="bottom"
                      sideOffset={8}
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      <Command>
                        <CommandInput placeholder="Buscar técnico..." />
                        <CommandList>
                          <CommandEmpty>No se encontraron técnicos.</CommandEmpty>
                          <CommandGroup>
                            {techniciansQuery.data?.map((tech: TechnicianRow) => (
                              <CommandItem
                                key={tech.id}
                                value={tech.name || tech.email}
                                onSelect={() => toggleTechnician(tech.id)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.assignedTechnicianIds.includes(tech.id) ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {tech.name} ({tech.email})
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Las fechas y vencimiento canónicos pertenecen al contrato. */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-1">Compromiso contractual</h3>
                {(() => {
                  const contract = installationsQuery.data?.find((row) => row.id === editingId);
                  const deadlineDetails = formatContractDeadlineDetails(contract);
                  return deadlineDetails ? (
                    <p className="text-xs text-muted-foreground">
                      {deadlineDetails.split("\n").map((line) => <span className="block" key={line}>{line}</span>)}
                      <span className="block">Las fases y fechas operativas se editan aquí.</span>
                    </p>
                  ) : contract?.contractId ? (
                    <p className="text-xs text-muted-foreground">Sin fecha límite contractual. Las fases y fechas operativas se editan aquí.</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin contrato canónico. La marca histórica de fechas fijas no se modifica desde este editor.</p>
                  );
                })()}
              </div>

              {/* Económico */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-1">Información Económica (Solo responsables autorizados)</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {isEditingCanonicalContractLinked ? (
                    <div className="md:col-span-2 xl:col-span-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                      La economía, los importes y la configuración de facturación pertenecen al contrato y no se editan desde la instalación.
                      Usa «Gestionar contrato y modificaciones» para consultar el workspace contractual.
                    </div>
                  ) : (
                    <>
                  <div className="grid gap-2">
                    <Label htmlFor="budget">Nº de Presupuesto / Ref</Label>
                    <Input id="budget" value={formData.budget} onChange={e => setFormData({ ...formData, budget: e.target.value })} />
                  </div>
                  <div className="grid gap-2 md:col-span-2 xl:col-span-1">
                    <Label htmlFor="installationPrice">Importe inicial del contrato (€)</Label>
                    {!editingId || canAssignMissingContractPrice ? (
                      <>
                        <Input id="installationPrice" type="number" step="0.01" value={formData.installationPrice} onChange={e => setFormData({ ...formData, installationPrice: e.target.value })} />
                        <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
                          {editingId
                            ? "Primera asignación del contrato original. Una vez guardado, quedará protegido."
                            : "Importe correspondiente al contrato original. Las ampliaciones o reducciones posteriores se gestionan mediante modificaciones contractuales."}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                          {formData.installationPrice ? `${Number(formData.installationPrice).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "Sin contrato original informado"}
                        </div>
                        <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
                          {isEditingContractPriceEstablished
                            ? "El contrato original no puede modificarse desde esta pantalla. Las ampliaciones y reducciones se gestionan en Contrato y modificaciones."
                            : "Debe solicitarse a administración la primera asignación del contrato original."}
                        </p>
                      </>
                    )}
                  </div>
                    </>
                  )}
                  <div className="grid gap-2">
                    <Label htmlFor="laborPrice">Coste mano de obra instaladores (€)</Label>
                    <Input id="laborPrice" type="number" step="0.01" value={formData.laborPrice} onChange={e => setFormData({ ...formData, laborPrice: e.target.value })} />
                    <p className="text-xs text-muted-foreground">Coste interno de instaladores; separado de subcontratas y del importe del contrato comercial.</p>
                  </div>
                </div>
                {editingId && canViewContract && (
                  <div className="rounded-md border bg-muted/30 p-4">
                    <h4 className="font-medium">Contrato</h4>
                    {editContractAdjustmentsQuery.isLoading || editContractCeilingQuery.isLoading ? (
                      <p className="mt-2 text-sm text-muted-foreground">Cargando resumen contractual...</p>
                    ) : editContractAdjustmentsQuery.isError || editContractCeilingQuery.isError ? (
                      <p className="mt-2 text-sm text-red-600">No se pudo cargar el resumen contractual.</p>
                    ) : (
                      <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                        <div>
                          <p className="text-muted-foreground">Importe inicial:</p>
                          <p className="font-semibold">{formatCents(editInitialCents)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Importe contractual vigente:</p>
                          <p className="font-semibold">{formatCents(editCurrentCents)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Modificaciones aprobadas:</p>
                          <p className="font-semibold">{formatCents(editContractSummary.approvedDeltaCents, { signed: true })}</p>
                        </div>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        setLocation(installationDetailPath(editingId!, filters, "tab=billing&section=contract-adjustments"));
                      }}
                    >
                      Gestionar contrato y modificaciones
                    </Button>
                  </div>
                )}
              </div>

              {/* Facturación - Solo para instalaciones y Admin/PM */}
              {/* Facturación - Solo para instalaciones y Admin/PM */}
              {showBillingConfig && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold border-b pb-1">Facturación</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="contractStatus">Estado del Contrato</Label>
                      <Select value={formData.contractStatus} onValueChange={(v) => setFormData({ ...formData, contractStatus: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract_pending">Pendiente</SelectItem>
                          <SelectItem value="contract_signed">Firmado</SelectItem>
                          <SelectItem value="contract_not_required">No requiere</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.contractStatus === 'contract_signed' && (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground ml-0">
                            Selecciona un archivo de contrato para subirlo ahora.
                          </p>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setSelectedContractFile(file);
                              }}
                              className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                            />
                          </div>
                          {selectedContractFile && (
                            <div className="flex items-center justify-between bg-muted/50 p-2 rounded text-xs">
                              <span className="truncate max-w-[200px]">{selectedContractFile.name}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedContractFile(null)}
                                className="text-red-500 hover:text-red-600"
                              >
                                Eliminar
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {formData.contractStatus === 'contract_pending' && (
                        <p className="text-xs text-muted-foreground ml-0">
                          Puedes firmar el contrato después desde la ficha de instalación.
                        </p>
                      )}
                      {formData.contractStatus === 'contract_not_required' && (
                        <p className="text-xs text-muted-foreground ml-0">
                          No se requiere contrato firmado para esta instalación.
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="billingConfigStatus">Validación de hitos</Label>
                      <Select value={formData.billingConfigStatus} onValueChange={v => setFormData({ ...formData, billingConfigStatus: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="billing_provisional">Provisional</SelectItem>
                          <SelectItem value="billing_validated">Validado</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.billingConfigStatus === 'billing_provisional' && (
                        <p className="text-xs text-blue-700 ml-0">
                          Puedes guardar y completar/validar después desde Facturación.
                        </p>
                      )}
                      {formData.billingConfigStatus === 'billing_validated' && (
                        <p className="text-xs text-blue-700 ml-0">
                          Los hitos quedan listos como configuración válida de facturación.
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="billingRequirementMode">Modelo de facturación</Label>
                      <Select value={formData.billingRequirementMode} onValueChange={v => setFormData({ ...formData, billingRequirementMode: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="milestones_required">Por hitos</SelectItem>
                          <SelectItem value="no_billing_required">Sin facturación</SelectItem>
                          <SelectItem value="optional_billable">Facturable opcional</SelectItem>
                          <SelectItem value="account_current_required">Cuenta corriente / recurrente</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="space-y-1">
                        {formData.billingRequirementMode === 'milestones_required' && (
                          <p className="text-xs text-muted-foreground ml-0">La instalación se factura por hitos definidos.</p>
                        )}
                        {formData.billingRequirementMode === 'no_billing_required' && (
                          <p className="text-xs text-muted-foreground ml-0">No se realizará seguimiento de facturación.</p>
                        )}
                        {formData.billingRequirementMode === 'optional_billable' && (
                          <p className="text-xs text-muted-foreground ml-0">Puede facturarse, pero no exige hitos obligatorios.</p>
                        )}
                        {formData.billingRequirementMode === 'account_current_required' && (
                          <p className="text-xs text-muted-foreground ml-0">Solo usar si existe acuerdo de facturación recurrente o bolsa abierta.</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {formData.billingConfigStatus === 'billing_provisional' && (
                    <p className="text-xs text-blue-700 ml-0">
                      Se puede guardar ahora y revisar/validar desde Facturación.
                    </p>
                  )}

                  {editingId ? (
                    <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                      Los hitos existentes no se duplican desde este formulario. Para gestionarlos, abre la ficha y entra en Facturación.
                      <Button type="button" variant="link" className="ml-1 h-auto p-0" onClick={() => setLocation(installationDetailPath(editingId!, filters, "tab=billing"))}>
                        Ir a Facturación
                      </Button>
                    </div>
                  ) : (
                    <div ref={initialMilestonesRef} className="space-y-3 rounded-md border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <h4 className="text-sm font-medium">Hitos iniciales</h4>
                          <p className="text-xs text-muted-foreground">Opcional salvo facturación validada con hitos obligatorios.</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={addInitialBillingMilestone}>
                          <Plus className="mr-2 h-4 w-4" /> Añadir hito
                        </Button>
                      </div>
                      {initialMilestonesError && (
                        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                          {initialMilestonesError}
                        </div>
                      )}
                      {initialBillingMilestones.map((milestone, index) => (
                        <div key={index} className="rounded-md border bg-muted/30 p-3 space-y-2">
                          <div className="flex items-start justify-between border-b pb-2">
                            <h5 className="text-sm font-semibold">Hito {index + 1}</h5>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeInitialBillingMilestone(index)} className="h-8 w-8 p-0 text-destructive" aria-label="Eliminar hito">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {/* Type selector */}
                            <div className="grid gap-1">
                              <Label className="text-xs">Tipo</Label>
                              <Select value={milestone.type} onValueChange={(v) => updateInitialBillingMilestone(index, 'type', v)}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="Tipo" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fixed_amount">Fijo</SelectItem>
                                  <SelectItem value="percentage">Porcentaje</SelectItem>
                                  <SelectItem value="monthly_certification">Certificación mensual</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Concepto</Label>
                              <Input value={milestone.label} onChange={e => updateInitialBillingMilestone(index, 'label', e.target.value)} placeholder="Concepto" className="h-9" />
                            </div>
                            {/* Warning for monthly_certification */}
                            {milestone.type === 'monthly_certification' && (
                              <div className="p-2 bg-amber-50 border border-amber-200 rounded">
                                <p className="text-xs text-amber-700">
                                  <strong>Nota:</strong> Este tipo permite importe vacío. El importe se definirá manualmente durante la certificación mensual.
                                </p>
                              </div>
                            )}
                            {/* Amount fields based on type */}
                            {milestone.type === 'percentage' ? (
                              <>
                                <div className="grid gap-1">
                                  <Label className="text-xs">Porcentaje (%)</Label>
                                  <Input type="number" step="0.1" min="0" max="100" value={milestone.percentage} onChange={e => updateInitialBillingMilestone(index, 'percentage', e.target.value)} placeholder="%" className="h-9" />
                                </div>
                                {/* Read-only calculated amount when installationPrice exists */}
                                <div className="grid gap-1">
                                  <Label className="text-xs">Importe calculado (€)</Label>
                                  <Input type="text" readOnly placeholder="Importe" value={(() => {
                                    const pct = parseFloat(milestone.percentage) || 0;
                                    const price = parseFloat(formData.installationPrice) || 0;
                                    return ((price * pct) / 100).toFixed(2);
                                  })()} className="h-9" />
                                </div>
                              </>
                            ) : milestone.type !== 'monthly_certification' ? (
                              <div className="grid gap-1">
                                <Label className="text-xs">Importe fijo (€)</Label>
                                <Input type="number" step="0.01" min="0" value={milestone.fixedAmount} onChange={e => updateInitialBillingMilestone(index, 'fixedAmount', e.target.value)} placeholder="Importe" className="h-9" />
                              </div>
                            ) : null}
                            <div className="grid gap-1">
                              <Label className="text-xs">Fecha prevista de facturación</Label>
                              <Input type="date" value={milestone.dueDate} onChange={e => updateInitialBillingMilestone(index, 'dueDate', e.target.value)} className="h-9" />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Base del plazo</Label>
                              <Select value={milestone.paymentTermBasis} onValueChange={(v) => updateInitialBillingMilestone(index, 'paymentTermBasis', v)}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="invoice_date">Fecha de factura</SelectItem>
                                  <SelectItem value="end_of_month">Fin de mes</SelectItem>
                                  <SelectItem value="client_acceptance">Aceptación cliente</SelectItem>
                                  <SelectItem value="other">Otro</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Plazo de pago (días)</Label>
                              <Input type="number" min="0" value={milestone.paymentTerms} onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                updateInitialBillingMilestone(index, 'paymentTerms', value);
                              }} placeholder="30" className="h-9" />
                              {milestone.dueDate && milestone.paymentTerms && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Vencimiento: {(() => {
                                    const due = new Date(milestone.dueDate);
                                    const days = parseInt(milestone.paymentTerms) || 0;
                                    due.setDate(due.getDate() + days);
                                    return due.toLocaleDateString();
                                  })()}
                                </p>
                              )}
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Forma de pago</Label>
                              <Select value={milestone.paymentMethod} onValueChange={(v) => updateInitialBillingMilestone(index, 'paymentMethod', v)}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="transfer">Transferencia</SelectItem>
                                  <SelectItem value="confirming">Confirming</SelectItem>
                                  <SelectItem value="promissory_note">Pagaré</SelectItem>
                                  <SelectItem value="other">Otro</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Descripción</Label>
                              <Textarea value={milestone.description} onChange={(e) => updateInitialBillingMilestone(index, 'description', e.target.value)} placeholder="Descripción" className="h-16" />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs">Notas</Label>
                              <Textarea value={milestone.notes} onChange={(e) => updateInitialBillingMilestone(index, 'notes', e.target.value)} placeholder="Notas adicionales" className="h-16" />
                            </div>
                          </div>
                        </div>
                      ))}
                      {formData.billingConfigStatus === 'billing_provisional' && initialBillingMilestones.length > 0 && (
                        <p className="text-xs text-blue-700 ml-0">
                          Los hitos iniciales se guardarán y podrán ajustarse desde Facturación.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeEditDialog}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={editingId === null || createWithBillingMutation.isPending || updateMutation.isPending || updateOperationalMutation.isPending || updateBillingConfigMutation.isPending}>
                {(createWithBillingMutation.isPending || updateMutation.isPending || updateOperationalMutation.isPending || updateBillingConfigMutation.isPending) ? "Guardando..." : (editingId ? "Actualizar" : "Crear Instalación")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
